import { Typography } from '@musinsa/mds';
import clsx from 'clsx';
import { CampaignInfo } from './CampaignInfo';

/**
 * 캠페인 유의사항 — 모든 시딩 상세 케이스 (recruit-01~09, propose-01~07) 공통.
 *
 * Figma node 2846-40484 (2026-06-23 개편):
 * - 구조: TitleSection(유의사항) + [소제목 + 불릿 목록] × 4 그룹
 * - 소제목 4개는 약관 동의 항목(seedingTerms)과 동일 제목 — 참여/저작권/광고표시/제세공과금
 *
 * 유의사항은 상세 본문의 마지막 섹션이라 하단 FAB/safe-area 여백(pb-72)을 기본 적용한다.
 * (디자인 요청으로 약관 동의 → 유의사항 순서가 되며 유의사항이 항상 마지막.) 마지막이 아닌
 * 곳에서 재사용할 경우 `className` 으로 pb 를 덮어쓸 수 있다(twMerge override).
 */
interface CampaignDetailNoticeProps {
  className?: string;
}

interface NoticeGroup {
  title: string;
  items: string[];
}

const NOTICE_GROUPS: NoticeGroup[] = [
  {
    title: '캠페인 참여 및 콘텐츠 제작·게시 의무',
    items: [
      '지정 기한 내 콘텐츠를 제작·업로드하고 URL을 제출해야 합니다.',
      '실제 사용·체험 후 진실한 의견으로 작성하며, 허위·과장·부당비교·비방이 금지됩니다.',
      '받은 상품을 판매·재판매·양도·교환·현금화할 수 없습니다.',
      '동시 진행 캠페인은 최대 10개이며, 초과 시 신규 신청이 제한될 수 있습니다.',
      '위 의무 또는 법령 위반, 그리고 부정 이용(허위정보·중복계정·미제출·표시누락·무단삭제·재판매·도용·권리침해 등) 시 서비스·캠페인 참여가 제한될 수 있으며, 이로 인하여 회사 또는 제3자에게 발생한 손해를 배상할 책임이 있습니다.',
    ],
  },
  {
    title: '콘텐츠 저작권 관한 내용',
    items: [
      '콘텐츠에 쓰는 타인 저작물·음원·초상 등의 권리는 본인이 직접 확보해야 하며, 제3자의 권리를 침해하는 콘텐츠를 제출해서는 안 됩니다.',
    ],
  },
  {
    title: '광고·표시(#광고) 의무 및 콘텐츠 가이드라인',
    items: [
      "게시물 제목 또는 본문 첫 줄 맨 앞에 잘 보이게 '#광고'를 표시해야 합니다.",
      '표시를 빠뜨리거나 소비자가 광고임을 알기 어렵게 한 경우, 그로 인하여 회사 및 제3자가 부담하게 되는 책임은 최종적으로 큐레이터에게 귀속됩니다.',
    ],
  },
  {
    title: '제세공과금 관련 내용',
    items: [
      '제세공과금은 브랜드사가 부담하며, 관련 신고 절차는 브랜드사가 진행합니다.',
      '제세공과금 신고를 위한 정보를 요청 받은 경우 관련 절차의 원활한 진행을 위해 필요한 정보를 제출해야 합니다.',
    ],
  },
];

export const CampaignDetailNotice = ({ className }: CampaignDetailNoticeProps = {}) => (
  <CampaignInfo.Container className={clsx('pb-[72px]!', className)}>
    <CampaignInfo.Title>유의사항</CampaignInfo.Title>
    {/* 간격은 컨테이너 책임 — 항목 padding 대신 gap-y, 가장자리는 Container padding.
        Figma node 2846-40484 환산: 타이틀~소제목 16(Container gap-y) / 그룹 사이 12 /
        그룹 내(소제목~불릿·불릿 간) 8. (.claude/rules/figma-list-spacing) */}
    <div className="flex flex-col gap-y-[12px]">
      {NOTICE_GROUPS.map((group) => (
        <div key={group.title} className="flex flex-col gap-y-[8px]">
          <Typography variant="body14pxMed" colorScheme="black">
            {group.title}
          </Typography>
          {group.items.map((item) => (
            <div key={item} className="flex gap-[4px] items-start">
              <Typography variant="body13pxReg" colorScheme="gray-600" className="shrink-0">
                •
              </Typography>
              <Typography variant="body13pxReg" colorScheme="gray-600" className="flex-1">
                {item}
              </Typography>
            </div>
          ))}
        </div>
      ))}
    </div>
  </CampaignInfo.Container>
);
